/**
 * The follower's authoring rules — D238 (`content-schema` `actor-follows.ts`;
 * Lyra's engine read of 2026-10-08, §1).
 *
 * @remarks
 * The schema already refuses the shapes that mean nothing — a follower that
 * stands in the way, a foe that follows, a refusal listing a room twice.
 * What is left needs the pack, and each rule is the reference walk or the
 * D31/D47 dead-content law applied to the new field:
 *
 * - **Every id is real.** `while`, each refusal's `when`, each refusal's
 *   lines and the rejoin lines walk like any condition and any narratives;
 *   a refusal's `roomIds` are declared rooms — a refusal of something that
 *   is not a room is a typo that would otherwise follow the player straight
 *   into the vacuum.
 * - **No refusal is shadowed.** Arms are read first match wins, so a room
 *   an earlier arm refuses with no `when` can never reach a later arm that
 *   names it again: those lines could never speak there.
 * - **Rejoin lines need a parting.** A follower that always follows
 *   (no `while`), refuses nothing, is never moved and starts where the
 *   player does is never found waiting — so its `rejoinNarratives` could
 *   never speak.
 *
 * Where the follower can then stand is not judged here: it is
 * `roomsAnActorCanStandIn`'s answer (content-schema `actor-stage.ts`), which
 * every other actor rule and the producibility walk already read.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorFollows, StageDirections } from '@orpheus/content-schema';

import { brokenReference } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, checkNarrativeLines, type DeclaredIdSets } from './reference-walkers.js';

/** What the follower's rules read of the pack beyond the actor itself. */
export interface FollowerContext {
	/** The pack's declared-id universe. */
	readonly declared: DeclaredIdSets;
	/** Where the player starts — a follower placed there and never parted from them is never found waiting. */
	readonly entryRoomId: string;
	/** The pack's stage directions — whether anything ever moves this body apart from the player. */
	readonly stage: StageDirections;
}

/**
 * Validate one actor's `follows`, if it declares one.
 *
 * @param actor - The actor
 * @param actorPath - Its path, for the failures
 * @param context - The pack facts the rules read
 * @param failures - Accumulator
 */
export const checkFollows = (actor: ActorDefinition, actorPath: string, context: FollowerContext, failures: ReferenceBrokenFailure[]): void => {
	const { follows } = actor;

	if (follows === undefined) {
		return;
	}

	const followsPath = `${actorPath}.follows`;

	if (follows.while !== undefined) {
		checkCondition(follows.while, `${followsPath}.while`, context.declared, failures);
	}

	const alwaysRefusedRoomIds = new Set<string>();

	follows.refuses.forEach((refusal, refusalIndex) => {
		const refusalPath = `${followsPath}.refuses[${String(refusalIndex)}]`;

		refusal.roomIds.forEach((roomId, roomIndex) => {
			const roomPath = `${refusalPath}.roomIds[${String(roomIndex)}]`;

			if (!context.declared.roomIds.has(roomId)) {
				failures.push(brokenReference(roomPath, roomId, 'the follower refuses a room the pack does not declare — a refusal of something that is not a room keeps it out of nowhere'));
			} else if (alwaysRefusedRoomIds.has(roomId)) {
				failures.push(
					brokenReference(
						roomPath,
						roomId,
						'an earlier refusal already keeps the follower out of this room with no `when` — arms are read first match wins, so this one could never speak here',
					),
				);
			}
		});

		if (refusal.when === undefined) {
			refusal.roomIds.forEach((roomId) => alwaysRefusedRoomIds.add(roomId));
		} else {
			checkCondition(refusal.when, `${refusalPath}.when`, context.declared, failures);
		}

		checkNarrativeLines(refusal.narratives, `${refusalPath}.narratives`, context.declared, failures);
	});

	checkNarrativeLines(follows.rejoinNarratives, `${followsPath}.rejoinNarratives`, context.declared, failures);

	if (follows.rejoinNarratives !== undefined && isNeverParted(actor, follows, context)) {
		failures.push(
			brokenReference(
				`${followsPath}.rejoinNarratives`,
				actor.actorId,
				'the follower is never parted from the player — it always follows (no `while`), refuses no room, is never moved and starts where the player does — so it is never found waiting, and these lines could never speak',
			),
		);
	}
};

/**
 * Is the follower with the player from the first page to the last — nothing
 * that could ever leave it behind, and nowhere to find it but at their side?
 *
 * @param actor - The actor
 * @param follows - Its `follows`
 * @param context - The pack facts the rule reads
 * @returns Whether no arrival can ever find it waiting
 */
const isNeverParted = (actor: ActorDefinition, follows: ActorFollows, context: FollowerContext): boolean =>
	follows.while === undefined &&
	follows.refuses.length === 0 &&
	actor.initialRoomId === context.entryRoomId &&
	!context.stage.actorMoves.some((move) => move.actorId === actor.actorId);
